// CloseMandateDialog · Lot M2
// Modal de clôture sans vente. reason obligatoire, comment obligatoire si `other`.

import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter }
  from "@/components/ui/dialog";

const REASONS = [
  { key: "expired",             label: "Mandat échu" },
  { key: "withdrawn",           label: "Retiré par le vendeur" },
  { key: "sold_by_seller",      label: "Vendu directement par le vendeur" },
  { key: "sold_elsewhere",      label: "Vendu ailleurs (autre canal)" },
  { key: "price_disagreement",  label: "Désaccord tarifaire" },
  { key: "agency_termination",  label: "Dénonciation par l'agence" },
  { key: "abandoned",           label: "Abandon" },
  { key: "other",               label: "Autre (commentaire requis)" },
];

export default function CloseMandateDialog({ open, onOpenChange, onSubmit }) {
  const [reason, setReason] = useState("expired");
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setReason("expired");
      setComment("");
    }
  }, [open]);

  const requiresComment = reason === "other";

  const submit = async (e) => {
    e.preventDefault();
    if (requiresComment && !comment.trim()) {
      return toast.error("Commentaire requis pour la clôture 'autre'");
    }
    setSubmitting(true);
    try {
      await onSubmit?.({ reason, comment: comment.trim() });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[520px]" data-testid="close-mandate-dialog">
        <DialogHeader>
          <DialogTitle className="text-[18px] font-semibold tracking-[-0.01em]">
            Clôturer le mandat sans vente
          </DialogTitle>
          <p className="text-[12.5px] text-black/50">
            Cette action est irréversible. Choisissez le motif exact pour l&apos;historique.
          </p>
        </DialogHeader>

        <form onSubmit={submit} className="mt-3 space-y-4">
          <label className="flex flex-col gap-1.5">
            <span className="text-[11px] uppercase tracking-[0.14em] text-black/55">
              Motif de clôture
            </span>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="h-10 rounded-lg bg-white px-3 text-[13.5px] outline-none focus:ring-2 focus:ring-[#C8FF2F]/40"
              style={{ boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.09)" }}
              data-testid="close-mandate-reason-select"
            >
              {REASONS.map((r) => (
                <option key={r.key} value={r.key}>{r.label}</option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-[11px] uppercase tracking-[0.14em] text-black/55">
              Commentaire{requiresComment && <span className="text-[#EF4444]"> *</span>}
            </span>
            <textarea
              rows={3}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={requiresComment ? "Précisez le motif exact..." : "Optionnel"}
              className="rounded-lg bg-white px-3 py-2 text-[13.5px] outline-none focus:ring-2 focus:ring-[#C8FF2F]/40"
              style={{ boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.09)" }}
              data-testid="close-mandate-comment-input"
            />
          </label>

          <DialogFooter className="mt-4 gap-2 sm:justify-end">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="rounded-full bg-white px-4 py-2 text-[13px] font-medium text-black/70 hover:text-black"
              style={{ boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.09)" }}
              data-testid="close-mandate-cancel-btn"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="rounded-full bg-[#B91C1C] px-5 py-2 text-[13px] font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-40"
              data-testid="close-mandate-submit-btn"
            >
              {submitting ? "Clôture…" : "Clôturer sans vente"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
